import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { netzBeobachten, netzStand } from '../api/client'
import { abonnieren, alle, senden, type OutboxEntry } from '../lib/outbox'

/**
 * Was in diesem Verlauf noch auf Netz wartet.
 *
 * Bewusst kein TanStack-Query: der Ausgangskorb ist kein Serverzustand, den
 * man neu holen könnte, sondern das Gegenteil davon -- die Wahrheit liegt im
 * Gerät, und geändert wird sie nur von hier.
 */
export function useOutbox(tripId: number, withUser?: number, entwurf = false): OutboxEntry[] {
  const [eintraege, setEintraege] = useState<OutboxEntry[]>([])

  useEffect(() => {
    let lebt = true
    const lesen = () => {
      void alle().then((liste) => {
        if (lebt) setEintraege(liste.filter((e) => passt(e, tripId, withUser, entwurf)))
      })
    }

    lesen()
    const abbestellen = abonnieren(lesen)
    return () => {
      lebt = false
      abbestellen()
    }
  }, [tripId, withUser, entwurf])

  return eintraege
}

/**
 * Was in **diesen** Verlauf gehört.
 *
 * Ein wartendes Highlight gehört in keinen: es ist kein Beitrag, und als
 * Sprechblase im Chat wäre es eine Nachricht, die nie eine war. Es steht auf
 * der Karte -- und im Streifen oben, der alles Wartende zählt.
 */
function passt(eintrag: OutboxEntry, tripId: number, withUser?: number, entwurf = false): boolean {
  return (
    (eintrag.art ?? 'nachricht') === 'nachricht'
    && eintrag.tripId === tripId
    && (eintrag.withUser ?? undefined) === withUser
    // A waiting draft stands in the draft (#85), not in the chat.
    && (eintrag.entwurf ?? false) === entwurf
  )
}

/**
 * Der Postbote: leert den Korb, wann immer sich eine Gelegenheit bietet.
 *
 * Drei Auslöser, und der dritte ist der auf iOS wichtigste: `online` kommt
 * beim Zurückkehren aus dem Hintergrund nicht verlässlich, `visibilitychange`
 * schon. Wer die App wieder aufmacht, ist genau der Augenblick, in dem eine
 * wartende Nachricht rausgehen soll.
 */
export function useOutboxVersand(): void {
  const client = useQueryClient()

  useEffect(() => {
    const versuchen = () => {
      void senden().then((raus) => {
        // Was rausging, ist jetzt eine richtige Nachricht -- der Verlauf und
        // die Liste sollen das sofort zeigen und nicht erst beim nächsten
        // Takt. Betroffen ist nur, was gerade offen ist; alles andere holt
        // sich seinen Stand ohnehin beim Öffnen.
        if (raus > 0) void client.invalidateQueries()
      })
    }

    versuchen()
    window.addEventListener('online', versuchen)
    const beiSicht = () => {
      if (document.visibilityState === 'visible') versuchen()
    }
    document.addEventListener('visibilitychange', beiSicht)
    // Und wenn irgendeine andere Anfrage wieder durchkommt: das ist der
    // verlässlichste Hinweis darauf, dass es wieder geht -- verlässlicher als
    // das `online`-Ereignis, das nur sagt, dass ein Netz da ist.
    const abbestellen = netzBeobachten(versuchen)

    return () => {
      window.removeEventListener('online', versuchen)
      document.removeEventListener('visibilitychange', beiSicht)
      abbestellen()
    }
  }, [])
}

/**
 * Wie viele Highlights dieser Reise noch auf Netz warten.
 *
 * Sie stehen nirgends auf der Karte -- es gibt sie beim Server ja noch nicht.
 * Ohne diese Zahl sähe es aus, als wäre das Anlegen ins Leere gegangen.
 */
export function useWartendeHighlights(tripId: number): number {
  const [anzahl, setAnzahl] = useState(0)

  useEffect(() => {
    let lebt = true
    const lesen = () => {
      void alle().then((liste) => {
        if (lebt) {
          setAnzahl(
            liste.filter((e) => e.art === 'highlight' && e.tripId === tripId).length,
          )
        }
      })
    }

    lesen()
    const abbestellen = abonnieren(lesen)
    return () => {
      lebt = false
      abbestellen()
    }
  }, [tripId])

  return anzahl
}

/** Wie viele Nachrichten insgesamt warten -- für den Streifen oben. */
export function useOutboxAnzahl(): number {
  const [anzahl, setAnzahl] = useState(0)

  useEffect(() => {
    let lebt = true
    const lesen = () => {
      void alle().then((liste) => {
        if (lebt) setAnzahl(liste.length)
      })
    }

    lesen()
    const abbestellen = abonnieren(lesen)
    return () => {
      lebt = false
      abbestellen()
    }
  }, [])

  return anzahl
}

/**
 * Ob gerade etwas durchgeht.
 *
 * Zwei Quellen, weil eine nicht reicht: die Ereignisse des Browsers
 * (`online`/`offline`) sagen es sofort, wenn jemand den Flugmodus umlegt --
 * behaupten aber „online", sobald irgendein WLAN da ist. Was tatsächlich
 * ankommt, weiß nur `netzStand()` aus den Anfragen selbst.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(netzStand)

  useEffect(() => {
    const pruefen = () => setOnline(netzStand())

    // Ein Wechsel auf „online" ist eine Behauptung, keine Zusage: der nächste
    // Abruf zeigt, ob wirklich etwas geht. Deshalb wird hier nur neu bewertet.
    window.addEventListener('online', pruefen)
    window.addEventListener('offline', pruefen)
    const abbestellen = netzBeobachten(pruefen)

    pruefen()
    return () => {
      window.removeEventListener('online', pruefen)
      window.removeEventListener('offline', pruefen)
      abbestellen()
    }
  }, [])

  return online
}
